/**
 * share — v2.1 分享管线（SCREENS.md §13）。
 *
 * 优先尝试 Web Share API（移动端原生分享面板）；不支持时打开应用内
 * 渠道面板，渠道矩阵按当前语言切换（zh: 复制/微博/QQ —— 微信无 web
 * intent，复制粘贴是生态惯例；en: X/Facebook/WhatsApp/Telegram）。
 * 所有渠道 URL 编码文案，新标签打开（noopener）。{url} 为部署源站，
 * 不带追踪参数（§13.4 非目标：无分享图、无深链、无渠道分析）。
 */
import { $settings } from '../store/settingsStore';
import { tr, trf, type DictKey } from '../ui/i18n';

export interface ShareContent {
  /** 分享文案（不含 URL；管线负责拼接与编码）。 */
  text: string;
}

/** 部署源站 URL（无追踪参数）。 */
export function shareUrl(): string {
  return `${location.origin}${location.pathname}`;
}

/** 纯邀请（标题菜单入口）。 */
export function plainInvite(): ShareContent {
  return { text: tr('sharePlainText', $settings.get().language) };
}

/** 派对邀请（含 3 位组队码）。 */
export function partyInvite(code: string): ShareContent {
  return { text: trf('shareInviteText', $settings.get().language, { code }) };
}

/** 战绩分享（结算入口）。 */
export function resultInvite(opts: {
  players: number;
  winnerName: string;
  maxCombo: number;
  performance: number;
}): ShareContent {
  return {
    text: trf('shareResultText', $settings.get().language, {
      n: opts.players,
      name: opts.winnerName,
      max: opts.maxCombo,
      pts: opts.performance,
    }),
  };
}

interface Channel {
  key: DictKey;
  /** null = 复制链接（微信/QQ 生态的粘贴惯例）。 */
  intent: ((text: string, url: string) => string) | null;
}

const CHANNELS_ZH: Channel[] = [
  { key: 'shareCopy', intent: null },
  {
    key: 'shareWeibo',
    intent: (t, u) =>
      `https://service.weibo.com/share/share.php?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}`,
  },
  {
    key: 'shareQQ',
    intent: (t, u) =>
      `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}`,
  },
];

const CHANNELS_EN: Channel[] = [
  { key: 'shareCopy', intent: null },
  {
    key: 'shareX',
    intent: (t, u) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}`,
  },
  {
    key: 'shareFacebook',
    intent: (_t, u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
  },
  {
    key: 'shareWhatsApp',
    intent: (t, u) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`,
  },
  {
    key: 'shareTelegram',
    intent: (t, u) =>
      `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
  },
];

/**
 * 分享入口：Web Share API 优先，退回应用内渠道面板。
 * @param host 面板挂载点（通常为 #ui）；toast 复用场景提示。
 */
export function share(content: ShareContent, host: HTMLElement, toast: (s: string) => void): void {
  const url = shareUrl();
  const lang = $settings.get().language;
  const fullText = `${content.text} ${url}`;

  if (typeof navigator.share === 'function') {
    navigator.share({ title: tr('title', lang), text: content.text, url }).catch(() => undefined); // 用户取消/不支持均静默（AbortError 属正常路径）
    return;
  }

  // ── 应用内渠道面板 ──
  const veil = document.createElement('div');
  veil.className = 'modal-veil share-veil';
  const modal = document.createElement('div');
  modal.className = 'modal share-sheet';
  const h = document.createElement('h2');
  h.textContent = tr('shareTitle', lang);
  const body = document.createElement('div');
  body.className = 'modal-body share-channels';

  const channels = lang === 'zh' ? CHANNELS_ZH : CHANNELS_EN;
  for (const ch of channels) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'menu-item share-channel';
    const name = document.createElement('span');
    name.className = 'mi-name';
    name.textContent = tr(ch.key, lang);
    btn.appendChild(name);
    btn.addEventListener('click', () => {
      if (ch.intent === null) {
        void navigator.clipboard
          ?.writeText(fullText)
          .then(() => toast(tr('shareCopied', $settings.get().language)))
          .catch(() => undefined);
      } else {
        window.open(ch.intent(content.text, url), '_blank', 'noopener');
      }
      close();
    });
    body.appendChild(btn);
  }

  const foot = document.createElement('div');
  foot.className = 'modal-foot';
  const done = document.createElement('button');
  done.type = 'button';
  done.className = 'btn';
  done.textContent = tr('netClose', lang);
  done.addEventListener('click', close);
  foot.appendChild(done);

  modal.append(h, body, foot);
  veil.appendChild(modal);
  host.appendChild(veil);

  function close(): void {
    veil.remove();
  }
  veil.addEventListener('click', (e) => {
    if (e.target === veil) close();
  });
}
